import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaCalendarCheck,
  FaCheckCircle,
  FaClock,
  FaCreditCard,
  FaDoorOpen,
  FaHotel,
  FaMobileAlt,
  FaQrcode,
  FaShieldAlt,
  FaStore,
  FaUsers,
} from 'react-icons/fa';

const RELEASE_MARKER = 'FORFEO_RESERVE_PRODUCTION_RELEASE_2026_08_03';

const merchantFeatures = [
  {
    icon: FaCalendarCheck,
    title: 'Calendrier marchand',
    text: 'Gérez les journées, heures, capacités, ressources et périodes bloquées depuis un même portail.',
  },
  {
    icon: FaClock,
    title: 'Disponibilités en temps réel',
    text: 'Les créneaux, holds temporaires et réservations utilisent une capacité contrôlée côté serveur.',
  },
  {
    icon: FaStore,
    title: 'Portail opérationnel',
    text: 'Consultez réservations, occupation, clients pseudonymisés, rapprochements et actions autorisées.',
  },
  {
    icon: FaCreditCard,
    title: 'Paiement officiel Forfeo',
    text: 'Les prix, taxes, frais, remboursements et événements Stripe restent exclusivement dans Forfeo.com.',
  },
];

const travelerFeatures = [
  {
    icon: FaHotel,
    title: 'Réserver une expérience',
    text: 'Choisissez un service, une date, une heure et une capacité réellement disponibles.',
  },
  {
    icon: FaMobileAlt,
    title: 'Parcours mobile',
    text: 'Une expérience responsive conçue pour réserver facilement à partir d’un téléphone.',
  },
  {
    icon: FaQrcode,
    title: 'Accès et pass Forfeo',
    text: 'Les confirmations, accès et validations restent liés au moteur transactionnel officiel.',
  },
  {
    icon: FaUsers,
    title: 'Expérience locale',
    text: 'Hôtels, restaurants, spas, activités et services locaux réunis dans un même univers.',
  },
];

export default function ForfeoReserve() {
  return (
    <main
      data-forfeo-reserve-release={RELEASE_MARKER}
      className="min-h-screen bg-slate-950 text-white"
    >
      <section className="relative overflow-hidden px-4 pb-20 pt-28 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.24),transparent_34%),radial-gradient(circle_at_80%_10%,rgba(14,165,233,0.20),transparent_30%)]" />
        <div className="relative mx-auto max-w-7xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-2 text-xs font-black uppercase tracking-[0.2em] text-emerald-200">
            <FaCheckCircle />
            Forfeo Reserve — disponible maintenant
          </div>

          <div className="mt-8 grid items-center gap-12 lg:grid-cols-[1.15fr_0.85fr]">
            <div>
              <h1 className="max-w-4xl text-4xl font-black tracking-tight sm:text-6xl lg:text-7xl">
                Réserver le meilleur du local,
                <span className="block bg-gradient-to-r from-emerald-300 via-cyan-300 to-sky-300 bg-clip-text text-transparent">
                  au bon moment.
                </span>
              </h1>
              <p className="mt-7 max-w-3xl text-lg font-medium leading-8 text-slate-300 sm:text-xl">
                Forfeo Reserve relie disponibilités, calendriers, capacités,
                politiques de réservation, portail marchand et moteur financier
                officiel dans un parcours unique.
              </p>

              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <Link
                  to="/business-dashboard?tab=reserve"
                  className="inline-flex items-center justify-center gap-3 rounded-2xl bg-emerald-400 px-6 py-4 font-black text-slate-950 shadow-xl shadow-emerald-500/20 transition hover:-translate-y-0.5 hover:bg-emerald-300"
                >
                  Ouvrir le portail marchand
                  <FaArrowRight />
                </Link>
                <Link
                  to="/mes-reservations"
                  className="inline-flex items-center justify-center gap-3 rounded-2xl border border-white/15 bg-white/5 px-6 py-4 font-black text-white transition hover:bg-white/10"
                >
                  Voir mes réservations
                </Link>
              </div>
            </div>

            <div className="rounded-[2rem] border border-white/10 bg-white/[0.06] p-6 shadow-2xl backdrop-blur-xl sm:p-8">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.2em] text-emerald-300">
                    Architecture officielle
                  </p>
                  <h2 className="mt-2 text-2xl font-black">Un parcours, trois autorités</h2>
                </div>
                <FaShieldAlt className="text-4xl text-emerald-300" />
              </div>
              <div className="mt-8 space-y-4">
                {[
                  ['Explore', 'Découverte, disponibilité et orchestration'],
                  ['Reserve', 'Holds, capacité et cycle de réservation'],
                  ['Forfeo.com', 'Prix, Stripe, taxes et remboursements'],
                ].map(([name, text]) => (
                  <div key={name} className="rounded-2xl border border-white/10 bg-slate-900/70 p-5">
                    <p className="font-black text-white">{name}</p>
                    <p className="mt-1 text-sm font-medium text-slate-400">{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-20 text-slate-950 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-emerald-700">
              Pour les partenaires
            </p>
            <h2 className="mt-3 text-3xl font-black sm:text-5xl">
              Toutes les opérations Reserve deviennent visibles.
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {merchantFeatures.map(({ icon: Icon, title, text }) => (
              <article key={title} className="rounded-3xl border border-slate-200 bg-slate-50 p-7 shadow-sm">
                <div className="inline-flex rounded-2xl bg-emerald-100 p-4 text-2xl text-emerald-700">
                  <Icon />
                </div>
                <h3 className="mt-6 text-xl font-black">{title}</h3>
                <p className="mt-3 text-sm font-medium leading-6 text-slate-600">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-cyan-300">
              Pour les voyageurs
            </p>
            <h2 className="mt-3 text-3xl font-black sm:text-5xl">
              Une réservation claire, mobile et sécurisée.
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {travelerFeatures.map(({ icon: Icon, title, text }) => (
              <article key={title} className="rounded-3xl border border-white/10 bg-white/[0.05] p-7">
                <div className="inline-flex rounded-2xl bg-cyan-300/10 p-4 text-2xl text-cyan-300">
                  <Icon />
                </div>
                <h3 className="mt-6 text-xl font-black">{title}</h3>
                <p className="mt-3 text-sm font-medium leading-6 text-slate-400">{text}</p>
              </article>
            ))}
          </div>

          <div className="mt-14 flex flex-col items-start justify-between gap-6 rounded-[2rem] bg-gradient-to-r from-emerald-400 to-cyan-300 p-8 text-slate-950 sm:flex-row sm:items-center sm:p-10">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.2em]">Forfeo Reserve</p>
              <p className="mt-2 text-2xl font-black sm:text-3xl">
                Le portail marchand Reserve est maintenant accessible depuis le site officiel.
              </p>
            </div>
            <Link
              to="/business-dashboard?tab=reserve"
              className="inline-flex shrink-0 items-center gap-3 rounded-2xl bg-slate-950 px-6 py-4 font-black text-white"
            >
              Accéder à Reserve
              <FaDoorOpen />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
